import { supabase } from '../lib/supabase';
import {
  getActualInfluencerPaidAmount,
  deriveInfluencerInvoiceStatus,
  getPaidReceivedAmount,
  getCreatorPaidAmount,
  computeCreatorFundedGross,
  computeCreatorNetReceived,
  getNetPayableBreakdown,
} from '../utils/paymentCalculations';
import { calculateInfluencerCommission, computeCampaignCommission, getCreatorTotalDue } from '../utils/commissionCalculations';
import { toLocalDateOnly, getCampaignReferenceDate } from '../utils/dates';
import { parseJsonArray } from '../lib/jsonColumn';

export interface PlatformSummary {
  [platform: string]: number;
}

export interface CreatorCampaignData {
  id: string;
  name: string;
  brand: string;
  client_name: string;
  status: string;
  delivery_status: string;
  start_date: string | null;
  end_date: string | null;
  agreed_fee: number;
  commission_pct: number;
  net_earnings: number;
  amount_received: number;
  campaign_influencer_id: string | null;
  campaign_influencer_status: string;
  deliverables_count: number;
  deliverables_published: number;
  deliverables_overdue: number;
  next_deadline: string | null;
  influencer_invoice_status: string;
  derived_influencer_invoice_status: string;
  influencer_invoices?: unknown;
  influencer_amount_paid: number;
  company_invoice_status: string;
  company_invoice_issued_date: string | null;
  updated_at: string | null;
  last_payment_date: string | null;
  is_installment_campaign: boolean;
  installment_total: number;
  installment_paid: number;
  platform_summary: PlatformSummary;
  year: number;
  company_payment_received_date: string | null;
  net_received: number;
  net_payable: number;
}

export interface CampaignInstallment {
  id: string;
  invoice_number: string;
  status: string;
  issued_date: string | null;
  due_date: string | null;
  amount_received: number;
  payment_received_date: string | null;
}

export interface CreatorDeliverable {
  id: string;
  campaign_id: string;
  campaign_influencer_id: string | null;
  platform: string | null;
  type: string | null;
  title: string;
  description: string | null;
  due_date: string | null;
  status: string;
  proof_url: string | null;
  published_url: string | null;
  published_at: string | null;
  is_overdue: boolean;
  fee: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreatorInvoice {
  id: string;
  campaign_id: string;
  campaign_influencer_id: string | null;
  type: string;
  number: string | null;
  issue_date: string | null;
  due_date: string | null;
  subtotal: number | null;
  vat_rate: number | null;
  total: number | null;
  currency: string | null;
  status: string;
  pdf_url: string | null;
  notes: string | null;
  is_overdue: boolean;
  has_tranche_schedule: boolean;
  total_tranches: number | null;
  tranches_paid_count: number | null;
  next_tranche_due_date: string | null;
  created_at: string;
}

export interface CampaignTimelineEvent {
  id: string;
  campaign_id: string;
  from_status: string;
  to_status: string;
  reason: string | null;
  created_at: string;
}

export interface QuarterlyTaxData {
  year: number;
  quarter: number;
  invoiceCount: number;
  totalLiquido: number;
  totalIVA: number;
  totalBruto: number;
}

export interface PaymentTranche {
  id: string;
  invoice_id: string;
  tranche_number: number;
  planned_amount: number;
  planned_date: string;
  paid_amount: number | null;
  paid_date: string | null;
  status: string;
  notes: string | null;
}

function normalizePlatform(raw: string | null): string {
  if (!raw) return 'Other';
  const lower = raw.toLowerCase();
  if (lower.includes('instagram') || lower.includes('ig ') || lower.includes('ig_') || /\big\b/.test(lower) || lower.includes('reels') || lower.includes('stories') || lower.includes('story')) return 'Instagram';
  if (lower.includes('tiktok') || lower.includes('tik tok')) return 'TikTok';
  if (lower.includes('youtube') || lower.includes('yt ') || /\byt\b/.test(lower)) return 'YouTube';
  if (lower.includes('twitter') || lower === 'x') return 'X';
  if (lower.includes('facebook') || lower.includes('fb')) return 'Facebook';
  if (lower.includes('podcast')) return 'Podcast';
  if (lower.includes('paid media')) return 'Paid Media';
  if (lower.includes('showcase') || lower.includes('evento') || lower.includes('event')) return 'Event';
  return 'Other';
}

function parseMohitInvoices(campaign: any): any[] {
  try {
    let invoices = campaign.mohit_invoices;
    if (typeof invoices === 'string') {
      invoices = JSON.parse(invoices);
      if (typeof invoices === 'string') {
        invoices = JSON.parse(invoices);
      }
    }
    if (Array.isArray(invoices)) return invoices;
  } catch {
    // fall through
  }
  return [];
}

function getReceivedAmount(campaign: any): number {
  const invoices = parseMohitInvoices(campaign);
  if (invoices.length > 0) {
    return invoices
      .filter((inv: { status?: string }) => inv.status === 'Paid')
      .reduce((total: number, inv: { amount_received?: number }) => {
        return total + (inv.amount_received || 0);
      }, 0);
  }
  return parseFloat(campaign.company_amount_received || '0');
}

function getInstallmentInfo(campaign: any): { total: number; paid: number; isInstallment: boolean; lastPaymentDate: string | null } {
  const invoices = parseMohitInvoices(campaign);
  if (invoices.length <= 1) {
    return { total: 0, paid: 0, isInstallment: false, lastPaymentDate: null };
  }
  const paid = invoices.filter((inv: any) => inv.status === 'Paid');
  let lastPaymentDate: string | null = null;
  for (const inv of paid) {
    const d = inv.payment_received_date || null;
    if (d && (!lastPaymentDate || d > lastPaymentDate)) lastPaymentDate = d;
  }
  return { total: invoices.length, paid: paid.length, isInstallment: true, lastPaymentDate };
}

export async function getCreatorAuth(userId: string) {
  const { data, error } = await supabase
    .from('influencer_auth' as any)
    .select('influencer_id, status')
    .eq('auth_user_id', userId)
    .maybeSingle();
  if (error) throw new Error('Failed to load account information');
  return data as { influencer_id: string; status: string } | null;
}

export async function getCreatorProfile(influencerId: string) {
  const { data, error } = await supabase
    .from('influencers')
    .select('*')
    .eq('id', influencerId)
    .single();
  if (error) throw new Error('Failed to load profile');
  return data as any;
}

export async function getCreatorCampaigns(influencerId: string): Promise<CreatorCampaignData[]> {
  const { data: allCampaigns, error: campaignsError } = await supabase
    .from('campaigns')
    .select(`
      id, campaign_ref, work_code, campaign_status, campaign_mode,
      client_name, brand, delivery_date, delivery_status, event_date, event_end_date,
      influencer_invoice_status, influencer_invoices, influencer_amount_paid,
      company_invoice_status, company_amount_received,
      mohit_invoices, fee, commission_pct, commission_type, commission_flat_fee,
      campaign_expenses, has_payables, campaign_budget,
      sponsorship_amount, agency_commission_percent, deliverables,
      company_invoice_issued_date, company_payment_received_date,
      created_at, updated_at, year
    `)
    .order('created_at', { ascending: false });

  if (campaignsError) throw new Error('Failed to load campaigns');
  if (!allCampaigns || allCampaigns.length === 0) return [];

  const campaignIds = (allCampaigns as any[]).map((c: any) => c.id);
  const imCampaignIds = (allCampaigns as any[])
    .filter((c: any) => c.campaign_mode === 'influencer_marketing')
    .map((c: any) => c.id);

  // Phase 1: batched queries (O(1) roundtrips)
  const [ciResult, delResult, allCiResult, invoicesResult] = await Promise.all([
    supabase
      .from('campaign_influencers')
      .select('*')
      .in('campaign_id', campaignIds)
      .eq('influencer_id', influencerId),
    supabase
      .from('deliverables')
      .select('campaign_id, status, due_date, is_overdue, platform')
      .in('campaign_id', campaignIds),
    // Total of ALL creators' fees per IM campaign (proportional-split denominator).
    // RLS hides co-creators' campaign_influencers rows from this session, so the
    // sum must come from the SECURITY DEFINER RPC (returns totals only, no fees
    // of individual co-creators).
    imCampaignIds.length > 0
      ? (supabase.rpc as any)('get_campaign_influencer_fee_totals', { p_campaign_ids: imCampaignIds })
      : Promise.resolve({ data: [] as any[], error: null }),
    // All invoices for these campaigns (for invoice_links matching)
    supabase
      .from('invoices')
      .select('id, campaign_id, campaign_influencer_id, type, status, total, counterparty_name')
      .in('campaign_id', campaignIds),
  ]);

  // Phase 2: fetch invoice_links for payable invoices (single batched query)
  const allInvoices = (invoicesResult.data || []) as any[];
  const payableInvoiceIds = allInvoices
    .filter((inv: any) => inv.type === 'Influencer')
    .map((inv: any) => inv.id);

  let invoiceLinks: any[] = [];
  if (payableInvoiceIds.length > 0) {
    const { data: linksData } = await supabase
      .from('invoice_links')
      .select('*')
      .in('payable_invoice_id', payableInvoiceIds);
    invoiceLinks = linksData || [];
  }

  // Build lookup maps
  const ciMap = new Map<string, any>();
  ((ciResult.data || []) as any[]).forEach((ci: any) => ciMap.set(ci.campaign_id, ci));

  // Total fees per IM campaign (for proportional ratio).
  // RPC rows are pre-aggregated: { campaign_id, total_agreed_fee }.
  // If the RPC is missing (migration not applied yet), fall back to the
  // caller's own row so the ratio degrades to 1.0 instead of crashing.
  const totalFeesMap = new Map<string, number>();
  if (!allCiResult.error && allCiResult.data) {
    (allCiResult.data as any[]).forEach((row: any) => {
      totalFeesMap.set(row.campaign_id, parseFloat(String(row.total_agreed_fee)) || 0);
    });
  }

  // Client invoices map: invoiceId -> { status, total }
  const clientInvoiceMap = new Map<string, { status: string; total: number }>();
  allInvoices
    .filter((inv: any) => inv.type === 'Client')
    .forEach((inv: any) => clientInvoiceMap.set(inv.id, { status: inv.status, total: inv.total || 0 }));

  // Payable invoices grouped by campaign_influencer_id (preferred) and counterparty_name (fallback)
  const payablesByCiId = new Map<string, string[]>();
  const payablesByName = new Map<string, Map<string, string[]>>();
  allInvoices
    .filter((inv: any) => inv.type === 'Influencer')
    .forEach((inv: any) => {
      // Prefer campaign_influencer_id matching
      if (inv.campaign_influencer_id) {
        const list = payablesByCiId.get(inv.campaign_influencer_id) || [];
        list.push(inv.id);
        payablesByCiId.set(inv.campaign_influencer_id, list);
      } else if (inv.counterparty_name) {
        // Fallback: match by counterparty_name within the same campaign
        const key = inv.campaign_id;
        if (!payablesByName.has(key)) payablesByName.set(key, new Map());
        const campMap = payablesByName.get(key)!;
        const list = campMap.get(inv.counterparty_name) || [];
        list.push(inv.id);
        campMap.set(inv.counterparty_name, list);
      }
    });

  // invoice_links by payable_invoice_id
  const linksByPayable = new Map<string, any[]>();
  invoiceLinks.forEach((link: any) => {
    const list = linksByPayable.get(link.payable_invoice_id) || [];
    list.push(link);
    linksByPayable.set(link.payable_invoice_id, list);
  });

  const delMap: Record<string, { total: number; published: number; overdue: number; nextDeadline: string | null; platforms: PlatformSummary }> = {};
  (delResult.data || []).forEach((d: any) => {
    if (!delMap[d.campaign_id]) {
      delMap[d.campaign_id] = { total: 0, published: 0, overdue: 0, nextDeadline: null, platforms: {} };
    }
    delMap[d.campaign_id].total++;
    if (d.status === 'Published') delMap[d.campaign_id].published++;
    if (d.is_overdue) delMap[d.campaign_id].overdue++;
    if (d.due_date && d.status !== 'Published') {
      const current = delMap[d.campaign_id].nextDeadline;
      if (!current || d.due_date < current) {
        delMap[d.campaign_id].nextDeadline = d.due_date;
      }
    }
    const plat = normalizePlatform(d.platform);
    delMap[d.campaign_id].platforms[plat] = (delMap[d.campaign_id].platforms[plat] || 0) + 1;
  });

  // Helper: compute exact allocated amount for a creator from invoice_links
  function getExactAllocatedForCreator(ciId: string | null, campaignId: string, creatorName: string): number | null {
    // Find this creator's payable invoice IDs (prefer ID match, fallback to name)
    let payableIds: string[] = [];
    if (ciId && payablesByCiId.has(ciId)) {
      // MATCHED BY ID
      payableIds = payablesByCiId.get(ciId)!;
    } else if (creatorName && payablesByName.has(campaignId)) {
      // FALLBACK: matched by counterparty_name
      payableIds = payablesByName.get(campaignId)?.get(creatorName) || [];
    }

    if (payableIds.length === 0) return null;

    // Sum allocated_amount from links where client invoice is Paid
    let totalAllocated = 0;
    let hasAnyLink = false;

    for (const payableId of payableIds) {
      const links = linksByPayable.get(payableId) || [];
      for (const link of links) {
        const clientInv = clientInvoiceMap.get(link.client_invoice_id);
        if (!clientInv || clientInv.status !== 'Paid') continue;
        hasAnyLink = true;
        // Constraint 2: cap at the actual received (client invoice total)
        const linkAmount = link.allocated_amount ?? clientInv.total;
        totalAllocated += Math.min(linkAmount, clientInv.total);
      }
    }

    return hasAnyLink ? totalAllocated : null;
  }

  const todayStr = toLocalDateOnly(new Date());
  return (allCampaigns as any[]).filter((c: any) => ciMap.has(c.id)).map((campaign: any) => {
    const ci = ciMap.get(campaign.id);
    let stats = delMap[campaign.id] || { total: 0, published: 0, overdue: 0, nextDeadline: null, platforms: {} };
    if (stats.total === 0) {
      // Fallback: deliverables stored in the campaigns.deliverables JSONB
      // column (brand_action/sponsorship forms) instead of the table.
      const jsonbDels = parseJsonArray<{ content?: string; date?: string; status?: string }>(campaign.deliverables);
      if (jsonbDels.length > 0) {
        stats = {
          total: jsonbDels.length,
          published: jsonbDels.filter(d => d.status === 'Published').length,
          overdue: jsonbDels.filter(d => !!d.date && d.date < todayStr && d.status !== 'Published').length,
          nextDeadline: jsonbDels
            .filter(d => !!d.date && d.status !== 'Published')
            .reduce<string | null>((min, d) => (!min || d.date! < min ? d.date! : min), null),
          platforms: {},
        };
      }
    }
    const isSponsorship = campaign.campaign_mode === 'sponsorship';

    let agreed_fee: number;
    let commission_pct: number;
    let commission: number;

    if (isSponsorship) {
      // Mirror the main app (campaignService.computeCampaignFields): the
      // sponsorship value and agency cut live on the campaign row, not on
      // campaign_influencers.
      agreed_fee = Number(campaign.sponsorship_amount) || 0;
      commission_pct = Number(campaign.agency_commission_percent) || 0;
      commission = agreed_fee * commission_pct;
    } else {
      agreed_fee = ci?.agreed_fee ?? campaign.fee ?? parseFloat(campaign.campaign_budget || '0');

      // Per-creator commission (constraint 4: always from ci, never campaign-level)
      const ciCommissionPct = ci?.commission_pct != null ? parseFloat(ci.commission_pct) : null;
      const ciCommissionFlatFee = ci?.commission_flat_fee != null ? parseFloat(ci.commission_flat_fee) : 0;
      const ciCommissionType = ci?.commission_type || 'percentage';

      // No phantom 30%: a missing rate means no commission, as everywhere else
      commission_pct = ciCommissionPct ?? (campaign.commission_pct != null ? parseFloat(campaign.commission_pct) : 0);
      // Creator-level terms when the row carries them (multi-influencer);
      // otherwise the campaign's own rule, which honours flat fees,
      // pass-through and revenue-only services.
      commission = ciCommissionPct != null || ciCommissionType === 'flat_fee'
        ? calculateInfluencerCommission(agreed_fee, commission_pct, ciCommissionFlatFee, ciCommissionType)
        : computeCampaignCommission(campaign);
    }

    const creatorName = ci?.influencer_name || '';
    const isIM = campaign.campaign_mode === 'influencer_marketing';
    const net_earnings = isIM
      ? agreed_fee - commission
      : getCreatorTotalDue({ ...campaign, commission_amount: commission });
    const influencer_amount_paid = isIM
      ? getCreatorPaidAmount(campaign, creatorName)
      : getActualInfluencerPaidAmount(campaign);

    const amount_received = getPaidReceivedAmount(campaign);

    let net_received: number;
    let net_payable: number;
    if (isIM) {
      // Use shared helpers with invoice_links exact allocation
      const totalCampaignFees = totalFeesMap.get(campaign.id) || agreed_fee;
      const exactAlloc = getExactAllocatedForCreator(ci?.id || null, campaign.id, creatorName);
      const { fundedGross } = computeCreatorFundedGross(
        amount_received, agreed_fee, totalCampaignFees, exactAlloc
      );
      net_received = computeCreatorNetReceived(fundedGross, commission, agreed_fee, net_earnings);
      net_payable = Math.max(0, net_received - influencer_amount_paid);
    } else {
      // Same figure the agency dashboard shows this creator
      const breakdown = getNetPayableBreakdown(campaign);
      net_received = breakdown.grossPayable + influencer_amount_paid;
      net_payable = breakdown.netPayable;
    }
    const derived_influencer_invoice_status = deriveInfluencerInvoiceStatus(campaign);

    const installment = getInstallmentInfo(campaign);

    return {
      id: campaign.id,
      name: campaign.campaign_ref || campaign.work_code || 'Unnamed Campaign',
      brand: campaign.brand || campaign.client_name || 'N/A',
      client_name: campaign.client_name || 'N/A',
      status: campaign.campaign_status || 'Consultation',
      delivery_status: campaign.delivery_status || 'Draft',
      start_date: getCampaignReferenceDate(campaign) || null,
      end_date: campaign.event_end_date || getCampaignReferenceDate(campaign) || null,
      agreed_fee,
      commission_pct,
      net_earnings,
      amount_received,
      campaign_influencer_id: ci?.id || null,
      campaign_influencer_status: ci?.status || 'Accepted',
      deliverables_count: stats.total,
      deliverables_published: stats.published,
      deliverables_overdue: stats.overdue,
      next_deadline: stats.nextDeadline,
      influencer_invoice_status: campaign.influencer_invoice_status || 'Not Requested',
      derived_influencer_invoice_status,
      influencer_invoices: campaign.influencer_invoices,
      influencer_amount_paid,
      company_invoice_status: campaign.company_invoice_status || 'Not Issued',
      company_invoice_issued_date: campaign.company_invoice_issued_date || null,
      updated_at: ci?.updated_at || campaign.updated_at || null,
      last_payment_date: installment.lastPaymentDate || campaign.company_payment_received_date || null,
      is_installment_campaign: installment.isInstallment,
      installment_total: installment.total,
      installment_paid: installment.paid,
      platform_summary: stats.platforms,
      year: campaign.year || new Date().getFullYear(),
      company_payment_received_date: campaign.company_payment_received_date || null,
      net_received,
      net_payable,
    };
  });
}

export async function getCampaignDeliverables(campaignId: string): Promise<CreatorDeliverable[]> {
  const { data, error } = await supabase
    .from('deliverables')
    .select('*')
    .eq('campaign_id', campaignId)
    .order('due_date', { ascending: true });
  if (error) throw new Error('Failed to load deliverables');
  if (data && data.length > 0) {
    return (data as any[]).map((d: any) => ({
      ...d,
      is_overdue: d.is_overdue ?? false,
      fee: d.fee ? parseFloat(String(d.fee)) : null,
      created_at: d.created_at || '',
      updated_at: d.updated_at || '',
    }));
  }

  // Brand action / sponsorship campaigns keep deliverables in the
  // campaigns.deliverables JSONB column instead of the deliverables table.
  // Surface them read-only (synthetic jsonb- ids have no table row, so proof
  // URLs cannot be edited on them).
  const { data: campaignRow } = await supabase
    .from('campaigns')
    .select('deliverables')
    .eq('id', campaignId)
    .maybeSingle();
  if (!campaignRow) return [];

  const today = new Date().toISOString().slice(0, 10);
  return parseJsonArray<{ content?: string; date?: string; status?: string }>((campaignRow as any).deliverables)
    .map((d, idx) => ({
      id: `jsonb-${idx}`,
      campaign_id: campaignId,
      campaign_influencer_id: null,
      platform: null,
      type: null,
      title: d.content || `Deliverable ${idx + 1}`,
      description: null,
      due_date: d.date || null,
      status: d.status || 'Draft',
      proof_url: null,
      published_url: null,
      published_at: null,
      is_overdue: !!d.date && d.date < today && d.status !== 'Published',
      fee: null,
      created_at: '',
      updated_at: '',
    }));
}

export async function getCampaignInvoices(campaignId: string): Promise<CreatorInvoice[]> {
  const { data, error } = await supabase
    .from('invoices')
    .select('*')
    .eq('campaign_id', campaignId)
    .order('created_at', { ascending: false });
  if (error) throw new Error('Failed to load invoices');
  return ((data || []) as any[]).map((inv: any) => ({
    ...inv,
    subtotal: inv.subtotal ? parseFloat(String(inv.subtotal)) : null,
    vat_rate: inv.vat_rate ? parseFloat(String(inv.vat_rate)) : null,
    total: inv.total ? parseFloat(String(inv.total)) : null,
    is_overdue: inv.is_overdue ?? false,
    has_tranche_schedule: inv.has_tranche_schedule ?? false,
    created_at: inv.created_at || '',
  }));
}

export async function getCampaignTimeline(campaignId: string): Promise<CampaignTimelineEvent[]> {
  const { data, error } = await supabase
    .from('campaign_status_events')
    .select('*')
    .eq('campaign_id', campaignId)
    .order('created_at', { ascending: true });
  if (error) return [];
  return (data || []) as any[];
}

export async function getPaymentTranches(invoiceId: string): Promise<PaymentTranche[]> {
  const { data, error } = await supabase
    .from('payment_tranches')
    .select('*')
    .eq('invoice_id', invoiceId)
    .order('tranche_number', { ascending: true });
  if (error) return [];
  return ((data || []) as any[]).map((t: any) => ({
    ...t,
    planned_amount: parseFloat(String(t.planned_amount)),
    paid_amount: t.paid_amount ? parseFloat(String(t.paid_amount)) : null,
  }));
}

export async function updateDeliverableProof(deliverableId: string, proofUrl: string) {
  const { error } = await (supabase as any)
    .from('deliverables')
    .update({ proof_url: proofUrl, updated_at: new Date().toISOString() })
    .eq('id', deliverableId);
  if (error) throw new Error('Failed to update proof URL');
}

export async function updateDeliverablePublishedUrl(deliverableId: string, publishedUrl: string) {
  const { error } = await (supabase as any)
    .from('deliverables')
    .update({
      published_url: publishedUrl,
      published_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', deliverableId);
  if (error) throw new Error('Failed to update published URL');
}

export function exportCreatorCampaignsCSV(campaigns: CreatorCampaignData[]): string {
  const headers = [
    'Campaign', 'Brand', 'Status', 'Deliverables', 'Gross Fee',
    'Commission %', 'Net Earnings', 'Payment Status', 'Invoice Status'
  ];
  const rows = campaigns.map(c => [
    c.name,
    c.brand,
    c.status,
    `${c.deliverables_published}/${c.deliverables_count}`,
    c.agreed_fee.toFixed(2),
    (c.commission_pct * 100).toFixed(0) + '%',
    c.net_earnings.toFixed(2),
    c.company_invoice_status,
    c.influencer_invoice_status,
  ]);
  const csvContent = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  return csvContent;
}

export function downloadCSV(csvContent: string, filename: string) {
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export async function getCampaignInstallments(campaignId: string): Promise<CampaignInstallment[]> {
  const { data, error } = await supabase
    .from('campaigns')
    .select('mohit_invoices')
    .eq('id', campaignId)
    .maybeSingle();
  if (error || !data) return [];
  const invoices = parseMohitInvoices(data);
  return invoices.map((inv: any) => ({
    id: inv.id || '',
    invoice_number: inv.invoice_number || '',
    status: inv.status || 'Not Issued',
    issued_date: inv.issued_date || null,
    due_date: inv.due_date || null,
    amount_received: inv.amount_received || 0,
    payment_received_date: inv.payment_received_date || null,
  }));
}

export async function getCreatorQuarterlyTaxData(
  campaignIds: string[],
  jsonbFallbackCampaigns?: { id: string; influencer_invoices?: unknown }[]
): Promise<QuarterlyTaxData[]> {
  if (campaignIds.length === 0) return [];

  const quarterMap = new Map<string, QuarterlyTaxData>();
  const addInvoice = (issueDate: string, total: number, subtotal: number, vatRate: number) => {
    const date = new Date(issueDate + 'T00:00:00');
    if (isNaN(date.getTime())) return;
    const year = date.getFullYear();
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    const key = `${year}-Q${quarter}`;

    const liquido = subtotal > 0 ? subtotal : total / (1 + vatRate);
    const iva = total - liquido;

    if (!quarterMap.has(key)) {
      quarterMap.set(key, { year, quarter, invoiceCount: 0, totalLiquido: 0, totalIVA: 0, totalBruto: 0 });
    }
    const entry = quarterMap.get(key)!;
    entry.invoiceCount++;
    entry.totalLiquido += liquido;
    entry.totalIVA += iva;
    entry.totalBruto += total;
  };

  const { data, error } = await supabase
    .from('invoices')
    .select('campaign_id, issue_date, subtotal, vat_rate, total, type')
    .in('campaign_id', campaignIds)
    .eq('type', 'Influencer')
    .not('issue_date', 'is', null)
    .gt('total', 0);

  // Campaigns already covered by normalized invoice rows — their JSONB copy
  // must be skipped to avoid double counting.
  const normalizedCampaignIds = new Set<string>();
  if (!error && data) {
    for (const inv of data as any[]) {
      normalizedCampaignIds.add(inv.campaign_id);
      const total = parseFloat(String(inv.total)) || 0;
      const vatRate = parseFloat(String(inv.vat_rate)) || 0.23;
      const subtotal = parseFloat(String(inv.subtotal)) || 0;
      addInvoice(inv.issue_date, total, subtotal, vatRate);
    }
  }

  // Fallback: brand_action/sponsorship campaigns store the creator's invoices
  // in the campaigns.influencer_invoices JSONB column instead of the invoices
  // table. Those campaigns belong to a single creator, so every entry is theirs.
  // Amounts are treated as gross with 23% IVA (the JSONB shape has no vat_rate).
  for (const campaign of jsonbFallbackCampaigns || []) {
    if (normalizedCampaignIds.has(campaign.id)) continue;
    const entries = parseJsonArray<{ issue_date?: string; amount?: number }>(campaign.influencer_invoices);
    for (const inv of entries) {
      const amount = Number(inv.amount) || 0;
      if (!inv.issue_date || amount <= 0) continue;
      addInvoice(inv.issue_date, amount, 0, 0.23);
    }
  }

  return Array.from(quarterMap.values()).sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.quarter - b.quarter;
  });
}
